import React, { useEffect, useRef } from 'react';

import { Card, Elevation, HTMLTable, Spinner } from '@blueprintjs/core';

import i18n from 'i18n/i18n';

import { AuditEntryDto } from '../../generated-sources/openapi';

import './AuditList.style.sass';

interface AuditListProps {
  title?: string;
  rows: AuditEntryDto[];
  loading: boolean;
  hasMore?: boolean;
  onReachBottom?: () => void;
  className?: string;
}

const AuditList: React.FC<AuditListProps> = ({
  title = i18n.t('audit-table.title'),
  rows,
  loading,
  hasMore = false,
  onReachBottom,
  className = '',
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const handler = () => {
      if (!onReachBottom || !hasMore) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
        onReachBottom();
      }
    };

    el.addEventListener('scroll', handler);
    return () => el.removeEventListener('scroll', handler);
  }, [onReachBottom, hasMore]);

  return (
    <Card className={`audit-card ${className}`} elevation={Elevation.ONE}>
      <h3 className="audit-card__title">{title}</h3>
      <div className="audit-card__table-wrapper" ref={wrapperRef}>
        <HTMLTable striped interactive className="audit-card__table">
          <thead>
            <tr>
              <th className="audit-card__th--date">{i18n.t('audit-table.date')}</th>
              <th className="audit-card__th--user">{i18n.t('audit-table.user')}</th>
              <th className="audit-card__th--field">{i18n.t('audit-table.field')}</th>
              <th className="audit-card__th--old">{i18n.t('audit-table.old')}</th>
              <th className="audit-card__th--new">{i18n.t('audit-table.new')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={`${r.eventId}-${r.field}`}>
                <td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}</td>
                <td>{r.createdBy}</td>
                <td>{r.subject ?? r.entityId ?? r.field}</td>
                <td>{r.oldValueDisplay ?? r.oldValue ?? ''}</td>
                <td>{r.newValueDisplay ?? r.newValue ?? ''}</td>
              </tr>
            ))}

            {loading && (
              <tr>
                <td colSpan={5}>
                  <Spinner size={16} />
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="audit-card__empty">
                  {i18n.t('audit-table.no-changes')}
                </td>
              </tr>
            )}
          </tbody>
        </HTMLTable>
      </div>
    </Card>
  );
};

export default AuditList;
